import React, { memo, useEffect, useState } from 'react'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { buildControlsExt, passwordInput } from '../../inputs/controls'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { inputDataDispatcher } from '../../../utils/InputDataDispatcher'
import { initInputData } from '../../../utils/InitInputData'
import { secretInputErrors } from '../../../utils/validationErrors/secretInputErrors'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { IStringInputSchema } from '../types'
import { PATH_JOINER } from '../utils/getConfigBitPath'
import { selectConfigBit, selectConfigIsChecked } from '../../../store/selectors/configSelectors'
import { getLabel } from '../../../utils/getLabel'

export interface ISecretProps {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: IStringInputSchema
    name: string
    wrapper?: string
}

export const Secret = memo(function Secret({
    dataPath,
    pathWithElementID,
    dataSchema,
    name,
    wrapper,
}: ISecretProps) {
    const dispatch = useAppDispatch()
    const configBit = useAppSelector(selectConfigBit(dataPath.split(PATH_JOINER)))
    const path = [...dataPath.split(PATH_JOINER), name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectConfigIsChecked)

    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
    } = dataSchema

    const validationCheck = inputValidationCheck(type, dataSchema)
    const validationSchema = inputValidationCheck(
        type,
        dataSchema,
        configBit?.[name]
    )
    const [guid, , tabName] = path

    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit, { [name]: validationSchema })

    const label: string = getLabel(name, inputLabel)
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const className: string = !wrapper ? 'w-50' : ''
    const errMsg = secretInputErrors(inputState?.[name], dataSchema)
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined
    const defaultValue = sessionStorage.getItem(errorKey)

    sessionStorage.setItem(errorKey, configBit?.[name])

    useEffect(() => {
        if (
            isChecked[guid] &&
            dataPath.length &&
            inputState[name] !== defaultValue
        ) {
            isValid()
        }
    }, [isChecked]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                guid,
                errorKey,
                isUndefined,
                checkPath,
                type,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        passwordInput({
            name,
            tooltipText,
            label,
            className,
            isColumn: true,
            errMsg,
            validationSchema,
            isRequired,
            inputType: 'secret',
            onBlur: handleOnBlur,
        }),
    ]

    const PasswordInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    guid,
                    errorKey,
                    data: configBit,
                    setInputState,
                    tabName,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length && inputState[name] !== defaultValue) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    guid,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked[guid]]) // eslint-disable-line

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{PasswordInput}</div>
})